import { isTextOnlySlice } from '@milkdown/kit/prose';
import type { EditorView } from '@milkdown/kit/prose/view';
import type { Node, Schema } from '@milkdown/kit/prose/model';
import { DOMParser, DOMSerializer, Fragment, Slice } from '@milkdown/kit/prose/model';

import type { MediaUploadHandler } from '../../types';
import {
  dataUriToFile,
  extractOrderedClipboardSegments,
  stripDataUriImagesFromHtml,
  stripDataUriImagesFromMarkdown,
  type ClipboardPasteSegment,
} from '../dataUriImageClipboard';
import type { ClipboardMarkdownParser } from '../tableCellImage';
import { isInTableCellPos } from '../tableCellHardBreak';
import { createMediaNode } from './createMediaNode';

function appendSegmentNodes(
  segment: ClipboardPasteSegment,
  schema: Schema,
  parser: ClipboardMarkdownParser,
  domParser: DOMParser,
  content: Node[],
): void {
  if (segment.kind === 'html') {
    const html = stripDataUriImagesFromHtml(segment.html);
    if (!html) return;
    const template = document.createElement('template');
    template.innerHTML = html;
    domParser.parseSlice(template.content.cloneNode(true)).content.forEach((node) => content.push(node));
    template.remove();

    return;
  }

  if (segment.kind === 'markdown') {
    const text = stripDataUriImagesFromMarkdown(segment.text);
    if (!text) return;
    const md = parser(text);
    if (!md || typeof md === 'string') return;
    domParser.parseSlice(DOMSerializer.fromSchema(schema).serializeFragment(md.content)).content.forEach((node) => content.push(node));
  }
}

/** Insert clipboard text and images in source order, then upload blob-backed images in place. */
export async function insertOrderedDataUriClipboardContent(
  view: EditorView,
  schema: Schema,
  parser: ClipboardMarkdownParser,
  clipboardData: DataTransfer,
  uploadImage: MediaUploadHandler,
  abortSignal: AbortSignal,
): Promise<void> {
  const segments = extractOrderedClipboardSegments(clipboardData);
  if (!segments.length) return;

  const asInlineImage = isInTableCellPos(view.state.selection.$from);
  const domParser = DOMParser.fromSchema(schema);
  const content: Node[] = [];
  const blobImages: { file: File; blobUrl: string }[] = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment.kind === 'image') {
      try {
        const file = await dataUriToFile(segment.dataUri, `pasted-image-${i + 1}`);
        const blobUrl = URL.createObjectURL(file);
        const node = createMediaNode(schema, 'image', blobUrl, file, asInlineImage);
        if (node) {
          content.push(node);
          blobImages.push({ file, blobUrl });
        }
      } catch (error) {
        console.error('[uploadPlugin] Failed to decode data-URI image:', error);
      }
      continue;
    }

    appendSegmentNodes(segment, schema, parser, domParser, content);
  }

  if (!content.length) return;

  const insertFrom = view.state.selection.from;
  const inserted = new Slice(Fragment.from(content), 0, 0);
  const textOnly = isTextOnlySlice(inserted);

  try {
    view.dispatch(
      textOnly
        ? view.state.tr.replaceSelectionWith(textOnly, true).scrollIntoView()
        : view.state.tr.replaceSelection(inserted).scrollIntoView(),
    );
  } catch {
    blobImages.forEach(({ blobUrl }) => URL.revokeObjectURL(blobUrl));

    return;
  }

  if (!blobImages.length || abortSignal.aborted) return;

  const insertTo = view.state.selection.to;
  const pending = [...blobImages];
  const uploadJobs: { file: File; blobUrl: string; pos: number }[] = [];

  view.state.doc.nodesBetween(insertFrom, insertTo, (node, pos) => {
    if (node.type.name !== 'image-block' && node.type.name !== 'image') return;

    const src = String(node.attrs.src ?? '');
    const matchIndex = pending.findIndex((item) => item.blobUrl === src);
    if (matchIndex === -1) return;

    const { file, blobUrl } = pending.splice(matchIndex, 1)[0]!;
    uploadJobs.push({ file, blobUrl, pos });
  });

  // Sequential uploads avoid exhausting the platform DB connection pool
  // (parallel attachments.create + attachments.redirect caused SequelizeConnectionAcquireTimeoutError).
  void (async () => {
    for (const { file, blobUrl, pos } of uploadJobs) {
      if (abortSignal.aborted || view.isDestroyed) return;

      try {
        const uploadedSrc = await uploadImage(file);
        if (abortSignal.aborted || view.isDestroyed) return;

        const current = view.state.doc.nodeAt(pos);
        if (!current || String(current.attrs.src ?? '') !== blobUrl) continue;

        view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, src: uploadedSrc }).scrollIntoView());
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('[uploadPlugin] Upload failed:', error);
        // Keep blob URL so the image still renders after a failed upload.
      }
    }
  })();
}
